/**
 * Appointment enums — status + type. Mirrored across mobile +
 * web (and the AppointmentCard component on mobile + the
 * Appointments form on web).
 */

export type AppointmentType =
  | 'site_visit'
  | 'office_meeting'
  | 'virtual_call'
  | 'other';

export const APPOINTMENT_TYPES: { key: AppointmentType; label: string }[] = [
  { key: 'site_visit', label: 'Site visit' },
  { key: 'office_meeting', label: 'Office meeting' },
  { key: 'virtual_call', label: 'Virtual call' },
  { key: 'other', label: 'Other' },
];

export function getAppointmentTypeLabel(t: AppointmentType): string {
  return APPOINTMENT_TYPES.find((x) => x.key === t)?.label ?? t;
}

export type AppointmentStatus =
  | 'scheduled'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export const APPOINTMENT_STATUSES: { key: AppointmentStatus; label: string }[] =
  [
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'no_show', label: 'No show' },
  ];

export function getAppointmentStatusLabel(s: AppointmentStatus): string {
  return APPOINTMENT_STATUSES.find((x) => x.key === s)?.label ?? s;
}

// ── Appointment form enums ───────────────────────────────────
// What the meeting is about + where it happens. Used by the
// appointment-create form on mobile + web.

/** What is the meeting ABOUT? Distinct dimension from `type`
 *  (which is the format — site visit, office, virtual). */
export type MeetingPurpose =
  | 'initial_consultation'
  | 'site_measurement'
  | 'design_presentation'
  | 'site_visit'
  | 'review'
  | 'handover'
  | 'other';

export const MEETING_PURPOSES: { key: MeetingPurpose; label: string }[] = [
  { key: 'initial_consultation', label: 'Initial Consultation' },
  { key: 'site_measurement',     label: 'Site Measurement' },
  { key: 'design_presentation',  label: 'Design Presentation' },
  { key: 'site_visit',           label: 'Site Visit' },
  { key: 'review',               label: 'Progress Review' },
  { key: 'handover',             label: 'Handover' },
  { key: 'other',                label: 'Other' },
];

export function getMeetingPurposeLabel(p: MeetingPurpose): string {
  return MEETING_PURPOSES.find((x) => x.key === p)?.label ?? p;
}

/** Where the meeting happens — independent of purpose. */
export type LocationMode = 'site' | 'office' | 'online' | 'other';

export const LOCATION_MODES: { key: LocationMode; label: string }[] = [
  { key: 'site',   label: 'At Client Site / Property' },
  { key: 'office', label: 'At Our Office' },
  { key: 'online', label: 'Online / Video Call' },
  { key: 'other',  label: 'Other' },
];

export function getLocationModeLabel(m: LocationMode): string {
  return LOCATION_MODES.find((x) => x.key === m)?.label ?? m;
}
